/** Per-scene palette for packed narration timelines (image / video / audio / subtitle lanes). */

import { mediaUrlForPlayback, mediaUrlForSegmentField, mediaUrlIdentityKey } from '@/lib/mediaUrls';
import { narrationSceneImagePlaybackUrl } from '@/lib/sceneGridNarrationAdapter';
import { visualBeatsWithImages } from '@/lib/visualBeatTimeline';

export const SCENE_BLOCK_PALETTE = [
  { image: '#d97706', video: '#4f46e5', audio: '#db2777', subtitle: '#16a34a' },
  { image: '#0891b2', video: '#7c3aed', audio: '#be123c', subtitle: '#15803d' },
  { image: '#ca8a04', video: '#2563eb', audio: '#c026d3', subtitle: '#0f766e' },
  { image: '#ea580c', video: '#6d28d9', audio: '#e11d48', subtitle: '#166534' },
  { image: '#0d9488', video: '#4338ca', audio: '#a21caf', subtitle: '#14532d' },
  { image: '#b45309', video: '#1d4ed8', audio: '#9d174d', subtitle: '#047857' },
];

export function sceneTimelineColors(sceneIndex) {
  const i = Number(sceneIndex);
  if (!Number.isFinite(i) || i < 0) return null;
  return SCENE_BLOCK_PALETTE[i % SCENE_BLOCK_PALETTE.length];
}

export function timelineClipSceneIndex(clip) {
  const n = Number(clip?.sceneColorIndex);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Stable scene/segment id for a timeline clip (video, image shadow, or audio). */
export function clipSceneSourceId(clip) {
  if (clip?.parentSceneId != null) return String(clip.parentSceneId);
  if (clip?.sceneId) return String(clip.sceneId);
  const raw = String(clip?.sourceClipId || clip?.id || '')
    .replace(/^audio-/, '')
    .replace(/^seg-audio-/, '')
    .replace(/__image$/, '');
  if (raw.includes('__ms')) return raw.split('__ms')[0];
  const segSplit = raw.split('-seg-')[0];
  return segSplit || raw;
}

export function clipMatchesSceneSource(clip, sourceId) {
  const sid = String(sourceId || '');
  if (!sid || !clip) return false;
  return clipSceneSourceId(clip) === sid;
}

/** Derive 1-based segment_number from clip metadata (name, then sceneColorIndex). */
export function segmentNumberFromTimelineClip(clip) {
  if (!clip || typeof clip !== 'object') return null;
  const m = String(clip?.name || '').match(/(?:scene|segment)\s*(\d+)/i);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const idx = timelineClipSceneIndex(clip);
  if (idx != null) return idx + 1;
  return null;
}

/** Segment id for image mapping. Ignores sceneColorIndex so a partial timeline cannot claim another scene's still. */
export function strictSegmentIdForClip(clip) {
  let raw = clipSceneSourceId(clip);
  raw = String(raw || '')
    .replace(/__image$/, '')
    .replace(/__beat\d*$/, '')
    .replace(/__ms\d*$/, '');
  if (raw.includes('-seg-')) raw = raw.split('-seg-')[0];
  return raw;
}

export function strictSceneForSegmentImage(clip, scenes = []) {
  const rows = Array.isArray(scenes) ? scenes : [];
  if (!rows.length || !clip) return null;
  const sourceId = strictSegmentIdForClip(clip);
  if (sourceId) {
    const byId = rows.find((s) => String(s.id) === sourceId);
    if (byId) return byId;
  }
  const m = String(clip?.name || '').match(/(?:scene|segment)\s*(\d+)/i);
  if (!m) return null;
  const segNum = Number(m[1]);
  if (!Number.isFinite(segNum) || segNum < 1) return null;
  return rows.find((s) => Number(s.scene_number ?? s.segment_number) === segNum) || null;
}

function audioWindowBySceneId(audioTracks, scenes) {
  const windows = {};
  (audioTracks || []).forEach((track) => {
    (track || []).forEach((clip) => {
      const scene = strictSceneForSegmentImage(clip, scenes);
      if (!scene?.id) return;
      const sid = String(scene.id);
      const start = Number(clip.startTime) || 0;
      const end = start + (Number(clip.duration) || 0);
      const prev = windows[sid];
      if (!prev) {
        windows[sid] = { start, end };
        return;
      }
      prev.start = Math.min(prev.start, start);
      prev.end = Math.max(prev.end, end);
    });
  });
  return windows;
}

/**
 * Put narration_segments.image_url on the primary video lane (same row the edit page shows).
 * Existing clips keep their start time. Segments that are missing from the lane are added
 * on top of that segment's audio window.
 */
export function stampSegmentImagesOntoVideoTracks(videoTracks, scenes = [], audioTracks = []) {
  const rows = [...(scenes || [])]
    .filter((scene) => scene?.id)
    .sort(
      (a, b) =>
        (Number(a.scene_number) || Number(a.segment_number) || 0)
        - (Number(b.scene_number) || Number(b.segment_number) || 0),
    );
  const tracks = (Array.isArray(videoTracks) && videoTracks.length ? videoTracks : [[]])
    .map((track) => (Array.isArray(track) ? track.map((clip) => ({ ...clip })) : []));
  if (!tracks.length) tracks.push([]);

  const windows = audioWindowBySceneId(audioTracks, rows);
  const seen = new Set();
  const linkClipToScene = (clip, scene) => {
    const sid = String(scene.id);
    const next = { ...clip, sceneId: sid };
    if (clip.parentSceneId != null) next.parentSceneId = sid;
    if (String(clip.id || '') === strictSegmentIdForClip(clip)) next.id = sid;
    return next;
  };
  tracks[0] = (tracks[0] || []).map((clip) => {
    const scene = strictSceneForSegmentImage(clip, rows);
    if (!scene?.id) return clip;
    const sid = String(scene.id);
    seen.add(sid);
    if (clip.visualBeat != null) {
      const beats = visualBeatsWithImages(scene);
      const beatUrl = String(beats[Number(clip.visualBeat)]?.imageUrl || '').trim();
      const segmentImage = sceneDocumentMediaPlayback(scene).imageUrl || '';
      const imageUrl = beatUrl || segmentImage;
      const linked = linkClipToScene(clip, scene);
      if (!imageUrl) return linked;
      return { ...linked, imageUrl, image_s3_url: null, image_local_url: null };
    }
    if (clip.multishotPart != null && clip.multishotIsMaster === false) {
      return linkClipToScene(clip, scene);
    }
    const imageUrl = sceneDocumentMediaPlayback(scene).imageUrl || '';
    const linked = linkClipToScene(clip, scene);
    if (!imageUrl) return linked;
    return { ...linked, imageUrl, image_s3_url: null, image_local_url: null };
  });

  let cursor = (tracks[0] || []).reduce(
    (max, clip) => Math.max(max, (Number(clip.startTime) || 0) + (Number(clip.duration) || 0)),
    0,
  );
  rows.forEach((scene, index) => {
    const sid = String(scene.id);
    if (seen.has(sid)) return;
    const imageUrl = sceneDocumentMediaPlayback(scene).imageUrl || '';
    if (!imageUrl) return;
    const window = windows[sid];
    const dur = window
      ? Math.max(0.5, window.end - window.start)
      : (Number(scene.audio_duration || scene.duration_seconds || scene.duration) || 5);
    const start = window ? window.start : cursor;
    tracks[0].push({
      id: sid,
      sceneId: sid,
      name: scene.title || `Segment ${scene.scene_number || scene.segment_number || ''}`.trim(),
      startTime: Math.round(start * 1000) / 1000,
      duration: dur,
      playbackDuration: dur,
      timelineSlotDuration: dur,
      trimStart: 0,
      trimEnd: dur,
      imageUrl,
      videoUrl: null,
      audioUrl: sceneDocumentMediaPlayback(scene).audioUrl || null,
      sceneColorIndex: index,
      narrationText: scene.narration_text || scene.scene_text || '',
      sceneText: scene.scene_text || scene.narration_text || '',
      muted: false,
      volume: 0.8,
    });
    seen.add(sid);
    if (!window) cursor += dur;
  });
  return tracks;
}

/** Match a timeline clip to the current server scene row (handles stale segment UUIDs). */
export function sceneForTimelineClip(clip, scenes = []) {
  const rows = Array.isArray(scenes) ? scenes : [];
  if (!rows.length) return null;
  const sourceId = clipSceneSourceId(clip);
  if (sourceId) {
    const byId = rows.find((s) => String(s.id) === String(sourceId));
    if (byId) return byId;
  }
  const segNum = segmentNumberFromTimelineClip(clip);
  if (segNum != null) {
    return rows.find((s) => Number(s.scene_number ?? s.segment_number) === segNum) || null;
  }
  return null;
}

/** Resolve stale timeline segment UUID → current narration_segments id. */
export function resolveNarrationSegmentId(sourceId, scenes = [], clip = null) {
  const rows = Array.isArray(scenes) ? scenes : [];
  const sid = String(sourceId || '').trim();
  if (sid && rows.some((s) => String(s.id) === sid)) return sid;
  const scene = clip ? sceneForTimelineClip(clip, rows) : null;
  if (scene?.id) return String(scene.id);
  if (!clip && sid) {
    const segNum = segmentNumberFromTimelineClip({ name: sid });
    if (segNum != null) {
      const byNum = rows.find((s) => Number(s.scene_number ?? s.segment_number) === segNum);
      if (byNum?.id) return String(byNum.id);
    }
  }
  return sid;
}

/** Rewrite clip.sceneId (and related ids) when segment UUIDs were regenerated server-side. */
export function remapTimelineClipToCurrentScene(clip, scenes = []) {
  if (!clip || typeof clip !== 'object') return clip;
  const scene = sceneForTimelineClip(clip, scenes);
  if (!scene?.id) return clip;
  const newId = String(scene.id);
  const linkedId = clipSceneSourceId(clip);
  if (linkedId === newId) return clip;
  const next = { ...clip, sceneId: newId };
  if (String(next.id) === linkedId) next.id = newId;
  if (String(next.id).startsWith('audio-')) next.id = `audio-${newId}`;
  if (String(next.id).endsWith('__image') && linkedId) next.id = `${newId}__image`;
  if (next.parentSceneId != null && String(next.parentSceneId) === linkedId) {
    next.parentSceneId = newId;
  }
  return next;
}

/** Canonical playback URLs from a scene/segment document (source of truth for editor media). */
export function sceneDocumentMediaPlayback(scene) {
  if (!scene) {
    return { videoUrl: null, imageUrl: null, audioUrl: null };
  }
  return {
    videoUrl: mediaUrlForSegmentField(scene, 'video_url') || null,
    // Same still resolver as Scene View / Workflow (image_url family + fallbacks).
    imageUrl: narrationSceneImagePlaybackUrl(scene) || null,
    audioUrl: mediaUrlForSegmentField(scene, 'audio_url') || null,
  };
}

/**
 * Still for the image timeline. Master clips use narration_segments (same row as /edit),
 * even when a saved editor clip still points at an older imageUrl or image_s3_url.
 */
export function imageUrlForTimelineClip(clip, scenes = []) {
  if (!clip || clip.isBookend) return '';
  const scene = sceneForTimelineClip(clip, scenes);
  const segmentImage = sceneDocumentMediaPlayback(scene).imageUrl || '';
  if (clip.visualBeat != null && scene) {
    const beats = visualBeatsWithImages(scene);
    const beatUrl = String(beats[Number(clip.visualBeat)]?.imageUrl || '').trim();
    if (beatUrl) return mediaUrlForPlayback(beatUrl) || beatUrl;
    return segmentImage || mediaUrlForPlayback(clip.imageUrl) || '';
  }
  if (clip.multishotPart != null && clip.multishotIsMaster === false) {
    const own = clip.imageUrl ? (mediaUrlForPlayback(clip.imageUrl) || '') : '';
    return own || segmentImage;
  }
  if (segmentImage) return segmentImage;
  return clip.imageUrl ? (mediaUrlForPlayback(clip.imageUrl) || '') : '';
}

/** Primary timeline video clip for a scene (excludes image-lane shadow rows). */
export function primaryVideoClipForScene(videoTracks, sourceId) {
  const sid = String(sourceId || '');
  for (const track of videoTracks || []) {
    for (const clip of track || []) {
      if (!clipMatchesSceneSource(clip, sid)) continue;
      if (clip.sourceClipId || String(clip.id || '').endsWith('__image')) continue;
      return clip;
    }
  }
  return null;
}

export function sceneDocumentVideoUrlChanged(clip, scene) {
  const { videoUrl: next } = sceneDocumentMediaPlayback(scene);
  const prev = clip?.videoUrl ? mediaUrlForPlayback(clip.videoUrl) : '';
  if (!next) return false;
  if (!prev) return true;
  return mediaUrlIdentityKey(prev) !== mediaUrlIdentityKey(next);
}

export function sceneDocumentImageUrlChanged(clip, scene) {
  const { imageUrl: next } = sceneDocumentMediaPlayback(scene);
  const prev = clip?.imageUrl ? mediaUrlForPlayback(clip.imageUrl) : '';
  if (!next) return false;
  if (!prev) return true;
  return mediaUrlIdentityKey(prev) !== mediaUrlIdentityKey(next);
}

function sceneMultishotImagesLocal(scene) {
  const raw = scene?.multishot_images || scene?.multishotImages || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row) => row && String(row.url || row.image_url || '').trim())
    .slice()
    .sort((a, b) => (Number(a.index) || 0) - (Number(b.index) || 0));
}

function enabledMultishotTimelinePartsLocal(scene) {
  const parts = [];
  const masterUrl = String(scene?.image_url || scene?.imageUrl || '').trim();
  if (scene?.multishot_include_master !== false && masterUrl) {
    parts.push({
      partIndex: parts.length,
      imageUrl: masterUrl,
      isMaster: true,
      shotIndex: null,
    });
  }
  sceneMultishotImagesLocal(scene)
    .filter((shot) => shot?.enabled !== false)
    .forEach((shot) => {
      parts.push({
        partIndex: parts.length,
        imageUrl: shot.url || shot.image_url,
        isMaster: false,
        shotIndex: Number(shot.index) || parts.length,
      });
    });
  return parts;
}

function multishotRowVideoUrl(shot) {
  const raw = shot?.video_url || shot?.video_s3_url || shot?.video_local_url;
  return raw ? mediaUrlForPlayback(raw) : null;
}

/** Resolve generated video for one multishot timeline slice using part index + shot row. */
export function resolveMultishotClipVideoFromScene(scene, clip) {
  if (!scene || !clip || clip.multishotPart == null) return null;

  const parts = enabledMultishotTimelinePartsLocal(scene);
  const partIdx = Number(clip.multishotPart);
  let part = parts.find((row) => row.partIndex === partIdx);
  if (!part && partIdx >= 0 && partIdx < parts.length) {
    part = parts[partIdx];
  }

  const isMaster = Boolean(clip.multishotIsMaster || part?.isMaster);
  if (isMaster) {
    return scene?.video_url ? mediaUrlForSegmentField(scene, 'video_url') : null;
  }

  const clipImage = clip.imageUrl || part?.imageUrl || null;
  const imgKey = clipImage ? mediaUrlIdentityKey(mediaUrlForPlayback(clipImage)) : '';
  const shots = sceneMultishotImagesLocal(scene);

  for (const shot of shots) {
    const shotUrl = shot?.url || shot?.image_url;
    if (!shotUrl) continue;
    const shotKey = mediaUrlIdentityKey(mediaUrlForPlayback(shotUrl));
    const idx = Number(shot.index ?? shot.shot_index);
    const matchesPartImage = part?.imageUrl
      && shotKey === mediaUrlIdentityKey(mediaUrlForPlayback(part.imageUrl));
    const matchesClipImage = imgKey && shotKey === imgKey;
    const matchesShotIndex = clip.multishotShotIndex != null
      && Number.isFinite(idx)
      && idx === Number(clip.multishotShotIndex);
    const matchesPartShotIndex = part?.shotIndex != null
      && Number.isFinite(idx)
      && idx === Number(part.shotIndex);
    if (!matchesPartImage && !matchesClipImage && !matchesShotIndex && !matchesPartShotIndex) {
      continue;
    }
    const videoUrl = multishotRowVideoUrl(shot);
    if (!videoUrl) return null;
    const sourceImg = shot.video_source_image_url || shot.source_image_url;
    if (imgKey && sourceImg) {
      const sourceKey = mediaUrlIdentityKey(mediaUrlForPlayback(sourceImg));
      if (sourceKey === imgKey) return videoUrl;
      if (matchesShotIndex || matchesPartShotIndex || matchesPartImage) return videoUrl;
      return null;
    }
    return videoUrl;
  }
  return null;
}

/** Resolve generated video for one multishot slice (legacy opts shape). */
export function resolveMultishotPartVideoUrl(scene, imageUrl, opts = {}) {
  return resolveMultishotClipVideoFromScene(scene, {
    multishotPart: opts.multishotPart ?? (opts.isMaster ? 0 : opts.shotIndex),
    multishotShotIndex: opts.shotIndex ?? null,
    multishotIsMaster: Boolean(opts.isMaster),
    imageUrl,
  });
}

/**
 * Resolve scene/segment video for a timeline clip (matches Scene Grid /edit: trust segment docs).
 * Uses strict stale-video checks only when the clip already carries a video URL.
 */
export function resolveSceneVideoUrlForTimelineClip(scene, clip) {
  if (!scene || !clip) return clip?.videoUrl || null;

  if (clip.parentSceneId != null && clip.multishotPart != null) {
    const fromShot = resolveMultishotClipVideoFromScene(scene, clip);
    if (fromShot) return fromShot;
    if (clip.multishotIsMaster && scene.video_url) {
      return mediaUrlForSegmentField(scene, 'video_url');
    }
    return clip.videoUrl || null;
  }

  const clipImage = clip.imageUrl
    || (scene.image_url ? mediaUrlForSegmentField(scene, 'image_url') : null);

  if (clip.videoUrl) {
    return resolveClipVideoUrlAfterImageChange(scene, clip, clipImage) || clip.videoUrl;
  }

  if (!scene.video_url) return null;
  const strict = resolveClipVideoUrlAfterImageChange(scene, { ...clip, videoUrl: null }, clipImage);
  return strict || mediaUrlForSegmentField(scene, 'video_url');
}

/** Drop stale generated video when the scene image changed but video still targets the old frame. */
export function resolveClipVideoUrlAfterImageChange(scene, existingClip, nextImageUrl) {
  if (existingClip?.parentSceneId != null && existingClip?.multishotPart != null) {
    const fromShot = resolveMultishotClipVideoFromScene(scene, {
      ...existingClip,
      imageUrl: nextImageUrl || existingClip.imageUrl,
    });
    if (fromShot) return fromShot;
    if (existingClip.videoUrl) return existingClip.videoUrl;
    return null;
  }

  const nextVideo = scene?.video_url ? mediaUrlForSegmentField(scene, 'video_url') : null;
  if (!nextVideo) return null;
  const sceneImageKey = nextImageUrl ? mediaUrlIdentityKey(nextImageUrl) : '';
  const sourceImg = scene?.video_source_image_url || scene?.source_image_url;
  if (sceneImageKey && sourceImg) {
    const sourceKey = mediaUrlIdentityKey(mediaUrlForPlayback(sourceImg));
    if (sourceKey === sceneImageKey) return nextVideo;
    if (!existingClip?.videoUrl) return nextVideo;
    return null;
  }
  if (!existingClip?.videoUrl) return nextVideo;
  const prevImageKey = existingClip.imageUrl
    ? mediaUrlIdentityKey(mediaUrlForPlayback(existingClip.imageUrl))
    : '';
  if (!sceneImageKey || prevImageKey === sceneImageKey) {
    return nextVideo || existingClip.videoUrl;
  }
  return null;
}
